import { TWorkflowById } from '@/domains/workflows/fetchers';
import { createBlocksTyped } from '@/lib/blocks/create-blocks-typed/create-blocks-typed';
import { valueOrNA } from '@ballerine/common';
import { useMemo } from 'react';
import { titleCase } from 'string-ts';
import { userCreatedIconCell } from '@/lib/blocks/utils/constants';

type DuplicationCheckOutput = {
  verdict?: string;
  is_duplicate?: boolean | string;
  duplicate_type?: string;
  reason?: string;
  report_summary?: string;
  face_similarity?: number | string;
  duplicate_of_user_id?: string;
  duplicate_of_user_email?: string;
  duplicate_of_case_id?: string;
  status?: string;
  journey_id?: string;
  attempt_id?: string;
  reason_code?: string;
  jurisdiction_source?: string;
  geo_mismatch?: string;
  name_match_status?: string;
  qale_outcome?: string;
  policy_version?: string;
  capture_quality?: string;
  ocr_status?: string;
  face_match_status?: string;
  didit_required?: string;
  didit_result_class?: string;
  didit_check_id?: string;
  document_authenticity_required?: string;
  external_document_result_class?: string;
  external_document_warning_codes?: string;
  case_create_status?: string;
  outbox_status?: string;
};

const FIELD_ORDER = [
  'verdict',
  'is_duplicate',
  'duplicate_type',
  'reason',
  'report_summary',
  'face_similarity',
  'duplicate_of_user_id',
  'duplicate_of_user_email',
  'duplicate_of_case_id',
  'status',
  'journey_id',
  'attempt_id',
  'reason_code',
  'jurisdiction_source',
  'geo_mismatch',
  'name_match_status',
  'qale_outcome',
  'policy_version',
  'capture_quality',
  'ocr_status',
  'face_match_status',
  'didit_required',
  'didit_result_class',
  'didit_check_id',
  'document_authenticity_required',
  'external_document_result_class',
  'external_document_warning_codes',
  'case_create_status',
  'outbox_status',
] as const;

/**
 * Report Summary should explain *why the case ended up where it did*:
 * - duplicate verdict → dedup text
 * - name mismatch / hard reject / revision → LiveKit reasoning
 * - otherwise → dedup uniqueness text
 */
function resolveDecisionSummary(
  workflow: TWorkflowById,
  duplicationCheck: DuplicationCheckOutput,
): { summary: string; headline: string } {
  const meta = workflow?.context?.metadata?.livekitVerificationResult as
    | Record<string, unknown>
    | undefined;
  const add = workflow?.context?.entity?.data?.additionalInfo as
    | Record<string, unknown>
    | undefined;

  const verdict = String(duplicationCheck.verdict ?? '').toLowerCase();
  const dedupText = String(
    duplicationCheck.report_summary || duplicationCheck.reason || '',
  ).trim();
  const reasoning = String(
    meta?.reasoning || add?.livekitReasoning || '',
  ).trim();
  const outcome = String(
    meta?.qaleOutcome || add?.qaleOutcome || '',
  ).toLowerCase();
  const nameStatus = String(
    meta?.nameMatchStatus || add?.nameMatchStatus || '',
  ).toLowerCase();
  const reasonCode = String(
    meta?.reasonCode || add?.reasonCode || duplicationCheck.reason_code || '',
  );

  if (verdict === 'duplicate' || duplicationCheck.is_duplicate === true) {
    return {
      headline: 'Duplicate',
      summary: dedupText || 'Duplicate identity detected.',
    };
  }

  if (nameStatus === 'mismatch') {
    return {
      headline: 'Name mismatch',
      summary:
        reasoning ||
        'Name on the ID does not match the signed-in account profile.',
    };
  }

  if (outcome === 'rejected') {
    return {
      headline: 'Rejected',
      summary: reasoning || dedupText || 'Verification was rejected.',
    };
  }

  if (outcome === 'needs_revision') {
    return {
      headline: 'Needs revision',
      summary: reasoning || 'A clearer ID capture is required.',
    };
  }

  if (reasonCode) {
    return {
      headline: titleCase(reasonCode.replace(/_/g, ' ')),
      summary: reasoning || dedupText || `Canonical reason: ${reasonCode}`,
    };
  }

  return {
    headline: valueOrNA(titleCase(String(duplicationCheck.verdict ?? 'completed'))),
    summary: dedupText || 'No duplicate identity found.',
  };
}

export const useDuplicationCheckBlock = ({ workflow }: { workflow: TWorkflowById }) => {
  return useMemo(() => {
    const duplicationCheck = workflow?.context?.pluginsOutput?.duplication_check as
      | DuplicationCheckOutput
      | undefined;

    if (!duplicationCheck || typeof duplicationCheck !== 'object') {
      return [];
    }

    const { summary, headline } = resolveDecisionSummary(workflow, duplicationCheck);
    const add = workflow?.context?.entity?.data?.additionalInfo as
      | Record<string, unknown>
      | undefined;
    const meta = workflow?.context?.metadata?.livekitVerificationResult as
      | Record<string, unknown>
      | undefined;
    const displayCheck: DuplicationCheckOutput = {
      ...duplicationCheck,
      reason: summary,
      report_summary: summary,
      journey_id: String(add?.verificationJourneyId || meta?.verificationJourneyId || ''),
      attempt_id: String(add?.verificationAttemptId || meta?.verificationAttemptId || ''),
      reason_code: String(add?.reasonCode || meta?.reasonCode || ''),
      jurisdiction_source: String(add?.jurisdictionSource || meta?.jurisdictionSource || ''),
      geo_mismatch: String(add?.geoMismatch ?? meta?.geoMismatch ?? ''),
      name_match_status: String(add?.nameMatchStatus || meta?.nameMatchStatus || ''),
      qale_outcome: String(add?.qaleOutcome || meta?.qaleOutcome || ''),
      policy_version: String(add?.policyVersion || meta?.policyVersion || ''),
      capture_quality: String(add?.captureQuality || meta?.captureQuality || ''),
      ocr_status: String(add?.ocrStatus || meta?.ocrStatus || ''),
      face_match_status: String(add?.faceMatchStatus || meta?.faceMatchStatus || ''),
      didit_required: String(
        add?.documentAuthenticityCheckRequired ??
          meta?.documentAuthenticityCheckRequired ??
          add?.diditRequired ??
          meta?.diditRequired ??
          '',
      ),
      didit_result_class: String(
        add?.externalDocumentResultClass ||
          meta?.externalDocumentResultClass ||
          add?.diditResultClass ||
          meta?.diditResultClass ||
          '',
      ),
      didit_check_id: String(
        add?.externalDocumentCheckId ||
          meta?.externalDocumentCheckId ||
          add?.diditCheckId ||
          meta?.diditCheckId ||
          '',
      ),
      document_authenticity_required: String(
        add?.documentAuthenticityCheckRequired ??
          meta?.documentAuthenticityCheckRequired ??
          add?.diditRequired ??
          meta?.diditRequired ??
          '',
      ),
      external_document_result_class: String(
        add?.externalDocumentResultClass ||
          meta?.externalDocumentResultClass ||
          add?.diditResultClass ||
          meta?.diditResultClass ||
          '',
      ),
      external_document_warning_codes: Array.isArray(add?.externalDocumentWarningCodes)
        ? (add?.externalDocumentWarningCodes as string[]).join(', ')
        : Array.isArray(meta?.externalDocumentWarningCodes)
          ? (meta?.externalDocumentWarningCodes as string[]).join(', ')
          : String(add?.externalDocumentWarningCodes || meta?.externalDocumentWarningCodes || ''),
      case_create_status: String(add?.caseCreateStatus || ''),
      outbox_status: String(add?.outboxStatus || ''),
    };

    const data = FIELD_ORDER.filter(
      key => displayCheck[key] != null && displayCheck[key] !== '',
    ).map(key => ({
      title: titleCase(key.replace(/_/g, ' ')),
      value: displayCheck[key],
      type: 'string' as const,
      isEditable: false,
    }));

    if (data.length === 0) {
      return [];
    }

    return createBlocksTyped()
      .addBlock()
      .addCell({
        type: 'block',
        value: createBlocksTyped()
          .addBlock()
          .addCell({
            type: 'container',
            value: createBlocksTyped()
              .addBlock()
              .addCell(userCreatedIconCell)
              .addCell({
                type: 'container',
                value: createBlocksTyped()
                  .addBlock()
                  .addCell({
                    type: 'heading',
                    value: 'Identity Duplication Check',
                    props: { className: 'mt-0' },
                  })
                  .addCell({
                    type: 'subheading',
                    value: headline,
                  })
                  .buildFlat(),
              })
              .buildFlat(),
            props: {
              className: 'flex space-x-1 items-center mt-4',
            },
          })
          .addCell({
            id: 'duplication-check-details',
            type: 'details',
            hideSeparator: true,
            value: {
              id: 'duplication-check-details-value',
              title: 'Identity Duplication Check',
              data,
            },
            workflowId: workflow?.id,
            documents: workflow?.context?.documents,
            isEditable: false,
          })
          .buildFlat(),
      })
      .build();
  }, [workflow]);
};
